/**
 * ERC-8004 Knowledge Graph Agent Page
 * Tab-based interface for database provisioning, Q&A, and orchestration
 */

import { useState, useEffect } from 'react';
import { ProvisionDatabaseStep } from '@/components/steps/ProvisionDatabaseStep';
import { IngestTab } from '@/components/tabs/IngestTab';
import { QATab } from '@/components/tabs/QATab';
import { OrchestrationTab } from '@/components/tabs/OrchestrationTab';
import type { TenantCredentials } from '@/types/database';

type TabType = 'provision' | 'ingest' | 'qa' | 'orchestrate';

export function KnowledgeGraphAgentPage() {
  const [activeTab, setActiveTab] = useState<TabType>('provision');
  const [credentials, setCredentials] = useState<TenantCredentials | null>(null);

  // Load saved credentials from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('kg_tenant_credentials');
    if (saved) {
      try {
        const creds = JSON.parse(saved);
        setCredentials(creds);
        // If credentials exist, switch to Q&A tab by default
        setActiveTab('qa');
      } catch (err) {
        console.error('Failed to parse saved credentials:', err);
      }
    }
  }, []);

  const handleProvisionComplete = (creds: TenantCredentials) => {
    setCredentials(creds);
    // Auto-switch to Ingest tab after provisioning
    setActiveTab('ingest');
  };

  const handleIngestComplete = () => {
    // Auto-switch to Q&A tab after ingestion
    setActiveTab('qa');
  };

  const tabs: { id: TabType; label: string; description: string }[] = [
    {
      id: 'provision',
      label: '1. Provision Database',
      description: 'Create your isolated KnowledgeFlowDB instance',
    },
    {
      id: 'ingest',
      label: '2. Ingest Data',
      description: 'Load ERC-8004 agent data from subgraph',
    },
    {
      id: 'qa',
      label: '3. Q&A',
      description: 'Ask questions about registered agents',
    },
    {
      id: 'orchestrate',
      label: '4. Orchestration',
      description: 'Create multi-agent execution plans',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            ERC-8004 Knowledge Graph Agent
          </h1>
          <p className="text-lg text-gray-600">
            Provision your database, explore agents, and orchestrate multi-agent workflows
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  py-4 px-1 border-b-2 font-medium text-sm transition-colors
                  ${
                    activeTab === tab.id
                      ? 'border-purple-500 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
                aria-current={activeTab === tab.id ? 'page' : undefined}
              >
                <div className="flex flex-col items-center">
                  <span className="font-semibold">{tab.label}</span>
                  <span className="text-xs mt-1 text-gray-400">{tab.description}</span>
                </div>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-8">
          {activeTab === 'provision' && (
            <ProvisionDatabaseStep onComplete={handleProvisionComplete} />
          )}

          {activeTab === 'ingest' && (
            <IngestTab credentials={credentials} onComplete={handleIngestComplete} />
          )}

          {activeTab === 'qa' && (
            <QATab credentials={credentials} onProvision={() => setActiveTab('provision')} />
          )}

          {activeTab === 'orchestrate' && (
            <OrchestrationTab
              credentials={credentials}
              onProvision={() => setActiveTab('provision')}
            />
          )}
        </div>
      </div>
    </div>
  );
}
